import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Phone,
  MapPin,
  Clock,
  TrendingUp,
  Store,
  Home,
  Gem,
  Sparkles,
  PiggyBank,
  Calendar,
  MessageCircle,
  History,
} from "lucide-react";
import { useRates } from "../contexts/RatesContext";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const location = useLocation();

  // Get live rates from context
  const { goldRates, silverRates, loading, formatRate, lastUpdated } =
    useRates();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const checkShopStatus = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.

      // Shop is open Monday-Saturday, 10:00 AM - 8:00 PM
      const isOpen =
        currentDay >= 1 &&
        currentDay <= 6 &&
        currentHour >= 10 &&
        currentHour < 20;
      setIsShopOpen(isOpen);
    };

    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
      checkShopStatus();
    }, 60000); // Update every minute

    checkShopStatus(); // Initial check
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearInterval(timeInterval);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const navItems = [
    { name: "Home", path: "/", icon: Home },
    { name: "Gold", path: "/gold", icon: Gem },
    { name: "Silver", path: "/silver", icon: Sparkles },
    { name: "Savings Schemes", path: "/savings-schemes", icon: PiggyBank },
    // { name: "Our Journey", path: "/our-journey", icon: History },
    // { name: "Offers", path: "/offers" },
    // { name: 'Collection', path: '/collection-demo' },
    // { name: "Book Now", path: "/book-now", icon: Calendar },
    { name: "Contact", path: "/contact", icon: MessageCircle },
  ];

  return (
    <>
      {/* Unified top header with rates, shop status, and contact info */}
      <div className="header-top-bars bg-[#1a2a39] text-white py-3 hidden md:block">
        <div className="container-custom px-4">
          <div className="flex items-center justify-between text-sm">
            {/* Left side - Shop Status */}
            <div className="flex items-center space-x-2 group">
              <Store
                className={`w-4 h-4 ${
                  isShopOpen ? "text-green-400" : "text-red-400"
                } group-hover:scale-110 transition-transform duration-300`}
              />
              <span
                className={`${
                  isShopOpen ? "text-green-400" : "text-red-400"
                } group-hover:scale-105 transition-transform duration-300`}
              >
                {isShopOpen ? "Shop Open" : "Shop Closed"}
              </span>
            </div>

            {/* Center - Rates Section */}
            <div className="flex items-center space-x-4 group">
              <TrendingUp className="w-5 h-5 text-gold group-hover:scale-110 transition-transform duration-300" />
              <div className="flex flex-col">
                <div className="flex items-center space-x-4 text-xs">
                  {loading ? (
                    // Loading state for rates
                    <>
                      <div className="flex flex-col items-center">
                        <span className="text-gold font-bold text-xs">18K</span>
                        <span className="text-white animate-pulse text-xs">
                          Loading...
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-gold font-bold text-xs">22K</span>
                        <span className="text-white animate-pulse text-xs">
                          Loading...
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-gold font-bold text-xs">24K</span>
                        <span className="text-white animate-pulse text-xs">
                          Loading...
                        </span>
                      </div>
                    </>
                  ) : (
                    // Live rates from API
                    <>
                      <div className="flex flex-col items-center hover:scale-105 transition-transform duration-300">
                        <span className="text-gold font-bold text-xs">18K</span>
                        <span className="text-white group-hover:text-gold transition-colors duration-300 text-xs">
                          {goldRates["18K"]
                            ? formatRate(goldRates["18K"])
                                .replace("₹", "₹")
                                .replace("/gram", "/g")
                            : "₹0/g"}
                        </span>
                      </div>
                      <div className="flex flex-col items-center hover:scale-105 transition-transform duration-300">
                        <span className="text-gold font-bold text-xs">22K</span>
                        <span className="text-white group-hover:text-gold transition-colors duration-300 text-xs">
                          {goldRates["22K"]
                            ? formatRate(goldRates["22K"])
                                .replace("₹", "₹")
                                .replace("/gram", "/g")
                            : "₹0/g"}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="w-px h-6 bg-gray-600"></div>
                  <div className="flex flex-col items-center hover:scale-105 transition-transform duration-300">
                    <span className="text-silver font-bold text-xs">
                      SILVER
                    </span>
                    <span className="text-white group-hover:text-silver transition-colors duration-300 text-xs">
                      {loading
                        ? "Loading..."
                        : silverRates["999"]
                        ? formatRate(silverRates["999"])
                            .replace("₹", "₹")
                            .replace("/gram", "/g")
                        : "₹0/g"}
                    </span>
                  </div>
                  {/* Last updated indicator - inline with silver */}
                  <div className="flex items-center space-x-2 text-xs text-gray-400">
                    <Clock className="w-3 h-3" />
                    {loading ? (
                      <div className="animate-pulse">
                        <div>--</div>
                        <div>--</div>
                      </div>
                    ) : lastUpdated ? (
                      <div className="text-center">
                        <div>
                          {new Date(lastUpdated).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                          })}
                        </div>
                        <div>
                          {new Date(lastUpdated).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div>--</div>
                        <div>--</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Remove the old updated indicator below */}
              </div>
            </div>

            {/* Right side - Contact Info and Hours */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 group">
                <Phone className="w-4 h-4 text-gold group-hover:scale-110 transition-transform duration-300" />
                <span className="group-hover:text-gold transition-colors duration-300">
                  +91 99761 57984
                </span>
              </div>
              <div className="flex items-center space-x-2 group">
                <Clock className="w-4 h-4 text-gold group-hover:scale-110 transition-transform duration-300" />
                <span className="group-hover:text-gold transition-colors duration-300">
                  Mon-Sat: 10:00 AM - 9:00 PM
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`header-main ${
          isScrolled
            ? "header-main-scrolled bg-[#1a2a39]/95 backdrop-blur-custom shadow-lg"
            : "header-main-normal bg-[#1a2a39]"
        }`}
      >
        <div className="container-custom px-4">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center space-x-3"
              onClick={closeMenu}
            >
              <div className="w-36 h-20 flex items-center justify-center">
                <img
                  src="/akila-logo.png"
                  alt="Akila Jewellers Logo"
                  className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    // Fallback to text logo if image fails to load
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="w-28 h-16 bg-[#1a2a39] rounded-lg flex items-center justify-center hidden">
                  <span className="text-white font-playfair font-bold text-2xl">
                    A
                  </span>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`font-medium transition-all duration-300 hover:text-gold hover:scale-105 transform flex items-center space-x-2 ${
                      location.pathname === item.path
                        ? "text-gold border-b-2 border-gold"
                        : "text-white"
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link
                to="/book-now"
                className="btn-primary hover:scale-105 hover:shadow-lg transition-all duration-300 transform"
              >
                Start Saving
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-white hover:text-gold transition-colors duration-300"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden transition-all duration-300 ease-in-out ${
            isMenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          } overflow-hidden bg-[#1a2a39] border-t border-gray-700`}
        >
          <nav className="container-custom px-4 py-4">
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`font-medium py-2 px-4 rounded-lg transition-all duration-300 flex items-center space-x-3 ${
                      location.pathname === item.path
                        ? "bg-gold text-white"
                        : "text-white hover:bg-gray-700 hover:text-gold"
                    }`}
                    onClick={closeMenu}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              <div className="pt-4 border-t border-gray-700">
                <Link
                  to="/book-now"
                  className="btn-primary w-full text-center block hover:scale-105 hover:shadow-lg transition-all duration-300 transform"
                  onClick={closeMenu}
                >
                  Start Saving
                </Link>
              </div>
            </div>
          </nav>
        </div>
      </header>

      {/* Spacer to prevent content from hiding behind fixed header */}
      <div className="h-24 md:h-32"></div>
    </>
  );
};

export default Header;
